import { useCallback, useEffect, useMemo, useState } from 'react'
import { Globe, Inbox, Send, SlidersHorizontal, Tags } from 'lucide-react'
import PageHeader from '../../components/PageHeader'
import PageLoader from '../../components/PageLoader'
import SegmentedControl from '../../components/SegmentedControl'
import useHashTab from '../../components/useHashTab'
import { useToast } from '../../components/toastContext'
import ImapProfilesPanel from './ImapProfilesPanel'
import PhishingEnginePanel from './PhishingEnginePanel'
import PhishingListPanel from './PhishingListPanel'
import SmtpProfilesPanel from './SmtpProfilesPanel'
import {
  deletePhishingCategory,
  deletePhishingDomain,
  exportPhishingDomains,
  getPhishingSettings,
  importPhishingDomains,
  listPhishingCategories,
  listPhishingDomains,
  savePhishingCategory,
  savePhishingDomain,
} from './phishingService'
import { phishingSettingsStrings as S } from './strings'
import './phishing.css'

const TABS = [
  { id: 'settings', label: S.tabs.settings, icon: SlidersHorizontal },
  { id: 'categories', label: S.tabs.categories, icon: Tags },
  { id: 'domains', label: S.tabs.domains, icon: Globe },
  { id: 'smtp', label: S.tabs.smtp, icon: Send },
  { id: 'imap', label: S.tabs.imap, icon: Inbox },
]

/** What the URL hash may name, so the command search can open any of the five. */
const TAB_IDS = TABS.map((tab) => tab.id)

/**
 * الإعدادات (صياد) — the legacy `PhishingCategories` screen.
 *
 * FIVE TABS, where the clone had four. The one that was missing is the one this slice is
 * actually about: the loose settings — the engine switch, the two service intervals, the
 * service URL, the ignored headers and the Outlook add-in — which are the only things
 * `/api/admin/phishing-settings` can write. The four table tabs were built first and three of
 * them had nothing behind them.
 *
 * THE LINKS TAB BELONGS TO ANOTHER SLICE. `PhishingDomains` is its own vertical slice with its
 * own routes, and the roadmap lists it after this one — but its only screen is this tab, and
 * leaving it mock while declaring this screen integrated would be worse than taking both. A
 * deliberate merge of two adjacent roadmap slices, written down here and in the roadmap.
 *
 * THE TWO MAILBOX TABS ARE THEIR OWN SLICES. They used to say they could not be served: the
 * campaigns payload could LIST a sender profile for picking and nothing more, and the legacy
 * screen's actions turned out to live in two controllers of their own rather than in
 * `PhishingCategoriesController`. Each now has a slice — `EmailSmtpSettings` and
 * `EmailImapSettings` — and the tabs are the lists over them.
 */
export default function PhishingSettingsPage() {
  const toast = useToast()
  const [tab, setTab] = useHashTab('settings', TAB_IDS)
  const [settings, setSettings] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    getPhishingSettings()
      .then((loaded) => {
        if (cancelled) return
        setSettings(loaded)
        setLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        setLoading(false)
        toast.error(error?.message || S.actionFailed)
      })
    return () => {
      cancelled = true
    }
  }, [toast])

  /* Stable identities: `PhishingListPanel` reloads whenever its `list` changes. */
  const listCategories = useCallback((options) => listPhishingCategories(options), [])
  const listDomains = useCallback((options) => listPhishingDomains(options), [])

  const categoryColumns = useMemo(
    () => [
      { key: 'nameAr', header: S.categories.name, render: (row) => <bdi>{row.nameAr}</bdi> },
      {
        key: 'nameEn',
        header: S.categories.nameEn,
        render: (row) => <bdi dir="ltr">{row.nameEn}</bdi>,
      },
    ],
    [],
  )

  const domainColumns = useMemo(
    () => [
      { key: 'name', header: S.domains.name, render: (row) => <bdi>{row.name}</bdi> },
      { key: 'url', header: S.domains.url, render: (row) => <span dir="ltr">{row.url}</span> },
    ],
    [],
  )

  return (
    <>
      <PageHeader
        title={S.title}
        subtitle={S.subtitle}
        scope={<SegmentedControl items={TABS} value={tab} onChange={setTab} ariaLabel={S.title} />}
      />

      {tab === 'settings' &&
        (loading || !settings ? (
          <PageLoader />
        ) : (
          <PhishingEnginePanel
            settings={settings}
            onChange={(patch) => setSettings((previous) => ({ ...previous, ...patch }))}
          />
        ))}

      {tab === 'categories' && (
        <PhishingListPanel
          stateKey="phishing-categories"
          columns={categoryColumns}
          fields={[
            { key: 'nameAr', label: S.categories.name },
            { key: 'nameEn', label: S.categories.nameEn, dir: 'ltr' },
          ]}
          labels={{ ...S.categoryForm, deleteConfirm: S.categoryForm.deleteConfirm }}
          withStatus
          list={listCategories}
          save={savePhishingCategory}
          remove={deletePhishingCategory}
        />
      )}

      {tab === 'domains' && (
        <PhishingListPanel
          stateKey="phishing-domains"
          columns={domainColumns}
          fields={[
            { key: 'name', label: S.domains.name },
            { key: 'url', label: S.domains.url, dir: 'ltr', type: 'url' },
          ]}
          labels={S.domainForm}
          list={listDomains}
          save={savePhishingDomain}
          remove={deletePhishingDomain}
          transfer={{
            onExport: exportPhishingDomains,
            onImport: importPhishingDomains,
            note: S.domainForm.importNote,
          }}
        />
      )}

      {tab === 'smtp' && <SmtpProfilesPanel />}

      {tab === 'imap' && <ImapProfilesPanel />}
    </>
  )
}
